import React, {useState} from "react";
import Styles from './styles.module.scss';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {setRefCode, setRefCodeFailure, setReferralModalStatus} from "../../store/cabinet/action";
import {formatNumber} from "../cabinetShell/config";

import heroArt from '../../theme/assets/redesign/referral/hero.webp';
import linkIcon from '../../theme/assets/redesign/referral/icon_link.svg';
import ticketIcon from '../../theme/assets/redesign/referral/icon_ticket.svg';
import userIcon from '../../theme/assets/redesign/referral/icon_user.svg';
import copyIcon from '../../theme/assets/redesign/referral/copy.svg';
import usersStatIcon from '../../theme/assets/redesign/referral/stat_users.svg';
import giftStatIcon from '../../theme/assets/redesign/referral/stat_gift.svg';
import chartStatIcon from '../../theme/assets/redesign/referral/stat_chart.svg';
import arrowUpIcon from '../../theme/assets/redesign/referral/arrow_up.svg';


// Поле «только для чтения» с подписью, иконкой и копированием (Copy Field из макета)
const CopyField = ({icon, label, value, tone = 'accent', onCopy}) => (
    <div className={`${Styles.field} ${Styles[`field_${tone}`]}`}>
        <img className={Styles.fieldIcon} src={icon} alt=""/>
        <div className={Styles.fieldText}>
            <div className={Styles.fieldLabel}>{label}</div>
            <div className={Styles.fieldValue}>{value}</div>
        </div>
        {onCopy ? (
            <button type="button" className={Styles.fieldCopy} onClick={onCopy} aria-label="copy">
                <img src={copyIcon} alt="" width="20" height="20"/>
            </button>
        ) : null}
    </div>
);

// Плитка статистики; дельта за неделю показывается, только если бэк её отдаёт
const StatTile = ({tone, icon, label, value, delta}) => (
    <div className={`${Styles.stat} ${Styles[`stat_${tone}`]}`}>
        <img className={Styles.statIcon} src={icon} alt=""/>
        <div className={Styles.statText}>
            <div className={Styles.statLabel}>{label}</div>
            <div className={Styles.statValueRow}>
                <span className={Styles.statValue}>{value}</span>
                {delta ? (
                    <span className={Styles.statDelta}>
                        <img src={arrowUpIcon} alt="" width="12" height="12"/>
                        {delta}
                    </span>
                ) : null}
            </div>
        </div>
    </div>
);


const CabinetReferralMain = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const referralsMain = useSelector(store => store.cabinet.referralsMain);
    const userData = useSelector(store => store.cabinet.userData);
    const formErr = useSelector(store => store.cabinet.refCodeStatusErr);

    const [code, setCode] = useState('');

    const refCode = referralsMain?.code || '';
    const refLink = refCode ? `${window.location.origin}/sign-up/ref/${refCode}` : '—';

    const copy = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        toast(t('Скопировано'));
    };

    const refHandler = () => {
        if (code.length > 4) {
            dispatch(setRefCode(code));
            setCode('');
        }
    };

    const received = Number(referralsMain?.received) || 0;
    const totalUsdt = received * (Number(userData?.token_rate) || 0);
    const weekSuffix = t('за неделю');
    const invitedDelta = referralsMain?.invited_week ? `+${referralsMain.invited_week} ${weekSuffix}` : null;
    const receivedDelta = referralsMain?.received_week ? `+${formatNumber(referralsMain.received_week)} ${weekSuffix}` : null;

    const fields = (
        <div className={Styles.fields}>
            <CopyField
                icon={linkIcon}
                label={t('Ваша реферальная ссылка')}
                value={refLink}
                onCopy={refCode ? () => copy(refLink) : null}
            />
            <CopyField
                icon={ticketIcon}
                label={t('Реферальный код')}
                value={refCode || '—'}
                onCopy={refCode ? () => copy(refCode) : null}
            />
        </div>
    );

    return (
        <>
            {/* ===== Hero ===== */}
            <section className={Styles.hero}>
                <div className={Styles.heroArt}>
                    <img src={heroArt} alt=""/>
                </div>
                <div className={Styles.title}>
                    <span>{t('Реферальная')}</span> <span>{t('программа')}</span>
                </div>
                <p className={Styles.heroText}>
                    {t('Приглашайте друзей, развивайте сообщество и получайте бонусы за активное участие. Чем больше людей вы приведёте, тем больше награда')}.
                </p>
                <button type="button" className={Styles.more} onClick={() => dispatch(setReferralModalStatus(true))}>
                    {`<< ${t('Подробнее')} >>`}
                </button>
                <div className={`${Styles.heroFields} ${Styles.mobileOnly}`}>{fields}</div>
            </section>

            {/* ===== Приглашение + пригласитель ===== */}
            <div className={Styles.inviteRow}>
                <section className={`${Styles.card} ${Styles.inviteCard} ${Styles.desktopOnly}`}>
                    <div className={Styles.cardTitle}>{t('Пригласите друзей и зарабатывайте больше')}:</div>
                    {fields}
                </section>

                <section className={`${Styles.card} ${Styles.inviterCard}`}>
                    <div className={Styles.cardTitle}>{t('Вы были приглашены')}?</div>
                    {referralsMain?.inviter ? (
                        <CopyField icon={userIcon} tone="danger" label={t('Ваш пригласитель')} value={`@${referralsMain.inviter}`}/>
                    ) : (
                        <div className={Styles.codeForm}>
                            <div className={`${Styles.codeInput} ${formErr?.code ? Styles.codeInputErr : ''}`}>
                                <img src={userIcon} alt="" width="24" height="24"/>
                                <input
                                    type="text"
                                    placeholder={t('Введите код приглашения')}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') refHandler();
                                        if (formErr?.code) {
                                            delete formErr.code;
                                            dispatch(setRefCodeFailure(formErr));
                                        }
                                    }}
                                />
                                <button type="button" className={Styles.apply} disabled={code.length <= 4} onClick={refHandler}>
                                    {t('Применить')}
                                </button>
                            </div>
                            {formErr?.code ? <div className={Styles.errText}>{formErr.code}</div> : null}
                        </div>
                    )}
                </section>
            </div>

            {/* ===== Статистика ===== */}
            <section className={`${Styles.card} ${Styles.statsCard}`}>
                <div className={Styles.cardTitle}>{t('Статистика')}</div>
                <div className={Styles.stats}>
                    <StatTile tone="danger" icon={usersStatIcon} label={t('Приглашено друзей')} value={formatNumber(referralsMain?.invited, 0)} delta={invitedDelta}/>
                    <StatTile tone="positive" icon={giftStatIcon} label={t('Получено FTF')} value={`${formatNumber(received)} FTF`} delta={receivedDelta}/>
                    <StatTile tone="info" icon={chartStatIcon} label={t('Общая стоимость (USDT)')} value={`$${formatNumber(totalUsdt)}`}/>
                </div>
            </section>
        </>
    );
};

export default CabinetReferralMain;
